/* eslint-disable react/no-did-update-set-state */
/* eslint-disable react/forbid-prop-types */
/* istanbul ignore file */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import Uri from 'urijs';
import _ from 'underscore';
import { SearchResultLiveRegion } from '@bingads-webui-react/search-box';
import { curlyTemplate } from '@bingads-webui-universal/primitive-utilities';
import { ListSelector } from '@bingads-webui-react/list-selector';
import { QueryParam, withScope } from '@bingads-webui-react/scope-service';
import { withUserConsumer } from '@bingads-webui-react/user-context';
import { siteMapPropType, getNewScope } from '@bingads-webui-react/with-site-map';
import { FallbackCallout } from '@bingads-webui-react/fallback-feature';
import { withI18nConsumer } from '@bingads-webui-react/i18n-context';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { Glyphicon } from '@bingads-webui-react/primitive';
import { getDefaultGlobalSearchStyles } from './default-global-search-styles';
import { getSuggestedPageResult, getPageSearchResult } from './utilities';
import { PagesSearchEngine } from './pages-search-engine';

class PageResultView extends React.PureComponent {
  constructor(props) {
    super(props);

    const {
      searchResultsNum,
      searchActivity,
      siteMap,
      i18n,
      searchEngines,
    } = props;

    this.pageResultNum = searchResultsNum;
    this.searchActivity = searchActivity;
    this.searchEngines = searchEngines || [
      new PagesSearchEngine({
        name: '',
        searchItemsTreeRoot: { children: siteMap },
        i18n,
      }),
    ];
    this.suggestedPages = this.getSuggestedPageResult();

    this.state = {
      selectedFallbackItem: {},
      searchPageResults: this.suggestedPages,
    };
  }

  componentDidUpdate(prevProps) {
    if (this.props.searchQuery !== prevProps.searchQuery) {
      if (this.props.searchQuery === '') {
        this.setState({
          searchPageResults: this.suggestedPages,
        });
        return;
      }

      const searchPageResults = getPageSearchResult({
        searchEngines: this.searchEngines,
        query: this.props.searchQuery,
      });
      this.setState({
        searchPageResults,
      });
    }
  }

  onSelect = (selectedItem) => {
    const {
      scope,
      routePrefix,
      userContext,
      clear,
    } = this.props;
    const { history } = scope;

    const searchItemUniqueId = this.getSearchItemUniqueId(selectedItem);

    this.searchActivity.trace(this.getSearchActivityTraceLog({
      isSearchResultSelected: true,
      selectedItem: searchItemUniqueId,
    }));

    if (selectedItem.available) {
      const prefix = _.result(selectedItem, 'routePrefix', routePrefix);
      const pathnameIsAbsolute = new Uri(selectedItem.route).is('absolute');
      const pathname = pathnameIsAbsolute ? selectedItem.route : prefix + selectedItem.route;
      history.push({
        pathname,
        search: scope.stringify(getNewScope(scope.values, selectedItem.scope, userContext)),
      });

      this.setState({ selectedFallbackItem: {} });
      clear();
    } else {
      this.setState(state => ({
        selectedFallbackItem:
          searchItemUniqueId === this.getSearchItemUniqueId(state.selectedFallbackItem)
            ? {}
            : selectedItem,
      }));
    }
  };

  getSearchItemUniqueId = item => item.route;

  getSuggestedPageResult = () => {
    if (!this.props.siteMap.length) {
      return [];
    }
    return getSuggestedPageResult({
      searchEngines: this.searchEngines,
      scope: this.props.scope,
      siteMap: this.props.siteMap,
    });
  };

  getSearchActivityTraceLog = ({
    isSearchResultSelected,
    selectedItem = '',
  }) => ({
    keyword: this.props.searchQuery,
    isSearchResultSelected,
    selectedItem,
  });

  handleCalloutClose = (item, event) => {
    event.stopPropagation();
    this.setState((state) => {
      if (this.getSearchItemUniqueId(item) ===
        this.getSearchItemUniqueId(state.selectedFallbackItem)) {
        return {
          selectedFallbackItem: {},
        };
      }
      return {};
    });
  };

  renderItem = (item) => {
    const { classes } = this.props;

    const showFallbackIcon = item.available !== true;

    return (
      <div
        className={classnames(classes.searchResultItem, 'global-search-result-item')}
      >
        <FallbackCallout
          title={this.state.selectedFallbackItem.name || ''}
          placement="right"
          classes={classes}
          show={item.showFallbackCallout}
          siteMapId={item.id}
          handleClose={e => this.handleCalloutClose(item, e)}
        >
          <div className={classes.searchItemContainer}>
            <div className={classes.searchItemSpan}><span title={item.name}>{item.name}</span></div>
            {item.shortcut && (
              <div className={classes.shortcutItem}>
                <div className={classes.shortcutKey}>{item.shortcut.split('+')[0].toUpperCase()}</div>
                <div className={classes.shortcutKey}>{item.shortcut.split('+')[1].toUpperCase()}</div>
              </div>)}
            {showFallbackIcon &&
            <Glyphicon
              className={showFallbackIcon ? classes.fallbackGlyph : ''}
              glyph="ba-OpenInNewWindow"
            />}
          </div>
        </FallbackCallout>
      </div>
    );
  };

  renderNoResultsListItem = (query) => {
    const { classes, i18n } = this.props;
    return (
      <div
        className={classnames(classes.searchResultItem, 'global-search-result-item')}
      >
        <div className={classes.searchItemContainer}>
          <div className={classes.searchItemSpan}>
            <span>{curlyTemplate(i18n.getString(_TL_('No results found for {{searchText}}')))({
              searchText: query,
            })}
            </span>
          </div>
        </div>
      </div>);
  };

  renderNoResults = (query, ariaLabel) => (
    <ListSelector
      renderHeader={this.renderHeader()}
      renderItem={() => this.renderNoResultsListItem(query)}
      renderDivider={_.noop}
      onSelect={_.noop}
      data={[{}]}
      ariaLabel={ariaLabel}
    />);

  renderHeader = (headerText) => {
    const { classes, i18n } = this.props;

    return () => (
      <div className={classnames(classes.resultHeaderContainer, 'global-search-result-section-header')}>
        <span className={classes.resultHeader} role="heading" aria-level="2">
          {headerText || i18n.getString(_TL_('Pages: '))}
        </span>
      </div>);
  };

  renderDivider = (item, index, key) => {
    const { classes } = this.props;
    return (
      <div
        className={classnames(classes.divider, 'global-search-divider')}
        key={key}
      />);
  };

  render() {
    const {
      classes,
      i18n,
      searchQuery,
    } = this.props;

    const {
      renderItem, renderHeader, onSelect, renderDivider,
    } = this;

    const {
      selectedFallbackItem,
    } = this.state;
    let searchResultItemCount = 0;

    const ariaLabel = i18n.getString(_TL_('Page search result list'));

    const listItemsToRender = this.state.searchPageResults
      .filter(section => section.items.length > 0)
      .map((searchResultSection) => {
        const items = searchResultSection.items.slice(0, this.pageResultNum);

        searchResultItemCount += items.length;

        const itemsWithDividers =
          items
            .map((item) => {
              const newItem = {
                ...item,
                showFallbackCallout: this.getSearchItemUniqueId(item) ===
                  this.getSearchItemUniqueId(selectedFallbackItem),
              };

              return [newItem, { type: 'divider' }];
            })
            .reduce((a, b) => a.concat(b), []);

        return { ...searchResultSection, items: itemsWithDividers };
      });
    return (
      <div className={classes.searchResultDropDown}>
        {
          listItemsToRender.length > 0
          && listItemsToRender.map(searchResultSection =>
            (
              <ListSelector
                renderHeader={renderHeader(searchResultSection.name)}
                renderItem={renderItem}
                renderDivider={renderDivider}
                onSelect={onSelect}
                data={searchResultSection.items}
                enableArrowKeysSupport
                ariaLabel={ariaLabel}
                key={`search-result-section-${searchResultSection.name}`}
              />
            ))
        }
        {searchQuery && listItemsToRender.length === 0 &&
        this.renderNoResults(searchQuery, ariaLabel)}
        <SearchResultLiveRegion
          totalCount={searchResultItemCount}
          emptyText={curlyTemplate(i18n.getString(_TL_('No results found for {{searchText}}')))({
            searchText: searchQuery,
          })}
          resultText={curlyTemplate(i18n.getString(_TL_('Found {{totalCount}} Results for {{searchText}}')))({
            totalCount: searchResultItemCount,
            searchText: searchQuery,
          })
        }
        />
      </div>);
  }
}

PageResultView.propTypes = {
  searchEngines: PropTypes.arrayOf(PropTypes.shape({ search: PropTypes.func })),
  classes: PropTypes.shape(_.mapObject(
    getDefaultGlobalSearchStyles({}, {}),
    _.constant(PropTypes.string.isRequired)
  )).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
  userContext: PropTypes.shape({
    CurrentAccount: PropTypes.object,
  }),
  clear: PropTypes.func.isRequired,
  routePrefix: PropTypes.string,
  scope: PropTypes.instanceOf(QueryParam).isRequired,
  searchActivity: PropTypes.any.isRequired,
  searchResultsNum: PropTypes.number.isRequired,
  siteMap: siteMapPropType.isRequired,
  searchQuery: PropTypes.string.isRequired,
};

PageResultView.defaultProps = {
  searchEngines: undefined,
  routePrefix: '',
  userContext: {},
};

export const StyledPageResultView =
withUserConsumer(withScope(withI18nConsumer(withDefaultStyles(
  PageResultView,
  getDefaultGlobalSearchStyles
))));

export const renderPageSearch = ({
  searchActivity,
  searchQuery,
  clear,
  searchResultsNum,
  searchEngines,
  siteMap,
}) => (
  <StyledPageResultView
    searchEngines={searchEngines}
    searchActivity={searchActivity}
    searchQuery={searchQuery}
    clear={clear}
    siteMap={siteMap}
    searchResultsNum={searchResultsNum}
    key="page-result-view"
  />
);

renderPageSearch.propTypes = {
  searchActivity: PropTypes.any.isRequired,
  searchResultsNum: PropTypes.number.isRequired,
  siteMap: siteMapPropType.isRequired,
  searchQuery: PropTypes.string.isRequired,
  clear: PropTypes.func.isRequired,
  // eslint-disable-next-line react/require-default-props
  searchEngines: PropTypes.arrayOf(PropTypes.shape({ search: PropTypes.func })),
};
